import type { BackdropFeature, MultiPolygonCoordinates, StructuralType } from '../models/editor-feature';
import type { BoundingBox } from './projection';

/**
 * A backdrop feature with what the renderer needs to decide how to paint it.
 */
export interface OrderedBackdropFeature {
  readonly feature: BackdropFeature;
  /**
   * Whether this covers enough of the plan to be the floor plate rather than something
   * standing on it — a slab, or the building's own footprint.
   */
  readonly isPlate: boolean;
}

/**
 * How much of the plan a feature covers before it is read as the floor plate rather than a
 * wall. The plates in the stored data cover 93–100% of their plan and the next largest
 * feature covers 17%, so anything in between separates them with room to spare.
 */
const PLATE_SHARE_OF_PLAN = 0.4;

const areaOf = (coordinates: MultiPolygonCoordinates): number => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  })));

  if (minX > maxX || minY > maxY) {
    return 0;
  }

  return (maxX - minX) * (maxY - minY);
};

/**
 * Puts the backdrop in painting order: the biggest shapes first, so the floor plate ends up
 * underneath everything standing on it.
 *
 * SVG paints in document order, and a stored layer's order is whatever its drawing tool
 * happened to emit. Some plans list the floor plate first and read correctly; others list it
 * last, where an opaque slab is painted straight over every wall already drawn. Same data,
 * same renderer, and the only difference is an array index — BDO Eindhoven storey 2 has its
 * plate at position 3 of 177 and looks right, while BDO Amstelveen storey 5 has it at 216 of
 * 216 and shows nothing but desks.
 *
 * Sorting by size can only reveal: a shape can now be hidden by a *smaller* one, which is
 * what being on top of the floor means, and never by a larger one covering the plan.
 */
export const backdropDrawOrder = (
  features: readonly (BackdropFeature & { readonly elementType?: StructuralType | null })[],
  planBounds: BoundingBox,
): OrderedBackdropFeature[] => {
  const planArea = Math.max(0, planBounds.maxX - planBounds.minX) * Math.max(0, planBounds.maxY - planBounds.minY);

  return features
    .map((feature) => {
      const area = areaOf(feature.coordinates);

      return {
        feature,
        area,
        // A shape that says what it is — a wall round the whole building, traced and then called
        // one — is that, however much of the plan it spans. Only an untyped shape is the plate.
        isPlate: !feature.elementType && planArea > 0 && area / planArea >= PLATE_SHARE_OF_PLAN,
      };
    })
    .sort((first, second) => second.area - first.area)
    .map(({ feature, isPlate }) => ({ feature, isPlate }));
};

export default backdropDrawOrder;
